import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';

interface ExpirationDatePickerProps {
  value?: string;
  onChange: (date: string) => void;
  onBlur?: (date: string) => void; // Called when user completes selection or input loses focus
  placeholder?: string;
  className?: string;
}

const ExpirationDatePicker: React.FC<ExpirationDatePickerProps> = ({
  value,
  onChange,
  onBlur,
  placeholder = "Set expiration date",
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [currentStep, setCurrentStep] = useState<'month' | 'day' | 'year'>('month');
  const [inputValue, setInputValue] = useState(value || '');
  const [isEditing, setIsEditing] = useState(false); // Track if user is actively editing
  const [validationError, setValidationError] = useState<string | null>(null); // Track validation errors
  const [dropdownPosition, setDropdownPosition] = useState<{
    horizontal: 'left' | 'right';
    top?: number;
    bottom?: number;
    left: number;
    maxHeight?: number;
  }>({ horizontal: 'left', top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isOpeningRef = useRef(false); // Track if we're in the process of opening dropdown

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const years = [2025, 2026, 2027, 2028, 2029, 2030];

  // Preset options for common expiration periods
  const presets = [
    { label: '160d', days: 160 },
    { label: '1y', months: 12, extraDays: 1 },
    { label: '2y', months: 24, extraDays: 1 },
    { label: '3y', months: 36, extraDays: 1 },
  ];

  const calculateDropdownPosition = () => {
    if (!inputRef.current) return;
    
    const rect = inputRef.current.getBoundingClientRect();
    const dropdownWidth = 320;
    const dropdownHeight = 400; // Estimated height for decision making
    const padding = 16;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // --- Horizontal Positioning (remains the same) ---
    let horizontalPosition: 'left' | 'right' = 'left';
    let left = rect.left;
    const fitsRight = rect.left + dropdownWidth <= viewportWidth - padding;
    const fitsLeft = rect.right - dropdownWidth >= padding;

    if (fitsRight) {
      left = rect.left;
    } else if (fitsLeft) {
      horizontalPosition = 'right';
      left = rect.right - dropdownWidth;
    } else {
      if ((viewportWidth - rect.left) >= rect.right) {
        left = padding;
      } else {
        horizontalPosition = 'right';
        left = viewportWidth - dropdownWidth - padding;
      }
    }
    
    // --- Vertical Positioning (new logic) ---
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        // Not enough space below, and more space above -> Open Upwards
        setDropdownPosition({
            horizontal: horizontalPosition,
            bottom: viewportHeight - rect.top + 4, // Anchor bottom of dropdown to top of input
            left: left,
            maxHeight: spaceAbove - padding // Constrain height to available space
        });
    } else {
        // Default to opening downwards
        setDropdownPosition({
            horizontal: horizontalPosition,
            top: rect.bottom + 4, // Anchor top of dropdown to bottom of input
            left: left,
            maxHeight: spaceBelow - padding // Constrain height to available space
        });
    }
  };

  // Sync local input value with prop value, but only when not actively editing
  useEffect(() => {
    if (!isEditing) {
      setInputValue(value || '');
    }
  }, [value, isEditing]);

  // Close dropdown when clicking outside and trigger save
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        // Trigger save when clicking outside
        setIsEditing(false); // Done editing
        setValidationError(null); // Clear validation error
        if (onBlur && inputValue !== value) {
          onBlur(inputValue);
        }
        setIsOpen(false);
        resetSelection();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        // Trigger save when pressing Escape
        setIsEditing(false); // Done editing
        setValidationError(null); // Clear validation error
        if (onBlur && inputValue !== value) {
          onBlur(inputValue);
        }
        setIsOpen(false);
        resetSelection();
      }
    };

    const handleResize = () => {
      if (isOpen) {
        calculateDropdownPosition();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
      window.addEventListener('resize', handleResize);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, inputValue, value, onBlur]);

  // Reset selection state
  const resetSelection = () => {
    setSelectedMonth(null);
    setSelectedDay(null);
    setSelectedYear(null);
    setCurrentStep('month');
  };

  const formatDate = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  const parseDate = (dateString: string): Date | null => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  };

  // Validate date and return error message if invalid
  const validateDate = (dateString: string): string | null => {
    if (!dateString || dateString.length === 0) {
      return null; // Empty is okay
    }

    // Check format (yyyy-mm-dd)
    const dateFormatRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateFormatRegex.test(dateString)) {
      if (dateString.length < 10) {
        return null; // Still typing, don't show error yet
      }
      return 'Invalid format (use yyyy-mm-dd)';
    }

    // Parse and validate the date
    const parts = dateString.split('-');
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);

    // Check if it's a valid date
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      return 'Invalid date';
    }

    // Check if date is in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) {
      return 'Date cannot be in the past';
    }

    // Check if date is too far in the future (e.g., more than 10 years)
    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() + 10);
    if (date > maxDate) {
      return 'Date too far in the future (max 10 years)';
    }

    return null; // Valid
  };

  const handlePresetClick = (preset: { label: string; months?: number; days?: number; extraDays?: number }) => {
    const futureDate = new Date();
    if (preset.days) {
      futureDate.setDate(futureDate.getDate() + preset.days);
    } else if (preset.months) {
      futureDate.setMonth(futureDate.getMonth() + preset.months);
      // Add extra days if specified (e.g., 1 year + 1 day)
      if (preset.extraDays) {
        futureDate.setDate(futureDate.getDate() + preset.extraDays);
      }
    }
    const formattedDate = formatDate(futureDate);
    setIsEditing(true); // Mark as editing to prevent prop sync
    setInputValue(formattedDate);
    setValidationError(validateDate(formattedDate)); // Validate immediately
    // Only update the local input value, don't notify parent yet
    // Parent will be notified on blur/close
  };

  const handleMonthClick = (monthIndex: number) => {
    setSelectedMonth(monthIndex);
    setCurrentStep('day');
  };

  const handleDateClick = (day: number) => {
    setSelectedDay(day);
    setCurrentStep('year');
  };

  const handleYearClick = (year: number) => {
    setSelectedYear(year);

    // Create the final date
    if (selectedMonth !== null && selectedDay !== null) {
      const date = new Date(year, selectedMonth, selectedDay);
      const formattedDate = formatDate(date);
      setIsEditing(true); // Mark as editing to prevent prop sync
      setInputValue(formattedDate);
      setValidationError(validateDate(formattedDate)); // Validate immediately
      // Only update the local input value, don't notify parent yet
      // Parent will be notified on blur/close
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value;

    // Remove any non-digit and non-dash characters
    input = input.replace(/[^\d-]/g, '');

    // Auto-format as yyyy-mm-dd
    if (input.length >= 4 && input.charAt(4) !== '-') {
      input = input.substring(0, 4) + '-' + input.substring(4);
    }
    if (input.length >= 7 && input.charAt(7) !== '-') {
      input = input.substring(0, 7) + '-' + input.substring(7);
    }

    // Limit to yyyy-mm-dd format (10 characters)
    if (input.length > 10) {
      input = input.substring(0, 10);
    }

    setIsEditing(true); // Mark as editing to prevent prop sync
    setInputValue(input);
    setValidationError(validateDate(input)); // Validate as user types
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Don't save if we're in the process of opening the dropdown
    if (isOpeningRef.current) {
      isOpeningRef.current = false;
      return;
    }

    // Don't trigger blur save if focus is moving within our component
    if (containerRef.current && e.relatedTarget && containerRef.current.contains(e.relatedTarget as Node)) {
      return;
    }

    // Input truly lost focus - save the changes only if value changed
    setIsEditing(false);
    setValidationError(null); // Clear validation error on blur

    // Only trigger callbacks if value actually changed
    if (inputValue !== value) {
      onChange(inputValue);
      if (onBlur) {
        onBlur(inputValue);
      }
    }
  };

  const handleInputClick = () => {
    if (!isOpen) {
      isOpeningRef.current = true; // Mark that we're opening, so blur doesn't save
      resetSelection();
      setIsEditing(true); // Start editing session
      // Small delay to ensure layout is stable before calculating position
      setTimeout(() => {
        calculateDropdownPosition();
        isOpeningRef.current = false; // Clear the flag after opening
      }, 0);
    }
    setIsOpen(!isOpen);
  };

  const getDaysInMonth = (month: number, year: number): number => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month: number, year: number): number => {
    return new Date(year, month, 1).getDay();
  };

  const renderCalendarGrid = () => {
    if (selectedMonth === null) return [];
    
    const currentYear = new Date().getFullYear();
    const daysInMonth = getDaysInMonth(selectedMonth, currentYear);
    const firstDay = getFirstDayOfMonth(selectedMonth, currentYear);
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-8"></div>);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const isToday = new Date().toDateString() === new Date(currentYear, selectedMonth, day).toDateString();
      const isSelected = selectedDay === day;
      
      days.push(
        <button
          key={day}
          onClick={() => handleDateClick(day)}
          className={`h-8 w-8 text-xs border border-gray-200 hover:bg-gray-100 ${
            isSelected ? 'bg-blue-500 text-white' : 
            isToday ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
          }`}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Input field */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onClick={handleInputClick}
          placeholder={inputValue ? '' : 'yyyy-mm-dd'}
          className={`w-full p-1 border h-7 text-xs bg-white cursor-pointer ${
            validationError ? 'border-red-400' : 'border-gray-300'
          }`}
        />

        {/* Validation error chip */}
        {validationError && (
          <div className="absolute left-0 top-full mt-1 z-50 flex items-center gap-1 px-2 py-1 bg-red-50 border border-red-200 rounded text-xs text-red-700 shadow-sm">
            <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span>{validationError}</span>
          </div>
        )}
      </div>

      {/* Custom calendar dropdown */}
      {isOpen && (
        <div className="fixed z-[9999] bg-white border border-gray-300 shadow-lg w-80 max-w-[calc(100vw-2rem)] overflow-hidden flex flex-col" 
          style={{ 
            minWidth: '320px',
            maxWidth: 'calc(100vw - 2rem)',
            maxHeight: dropdownPosition.maxHeight ? `${dropdownPosition.maxHeight}px` : undefined,
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
            left: `${dropdownPosition.left}px`,
            ...(dropdownPosition.top !== undefined && { top: `${dropdownPosition.top}px` }),
            ...(dropdownPosition.bottom !== undefined && { bottom: `${dropdownPosition.bottom}px` }),
          }}
        >
          {/* Header with close button */}
          <div className="flex justify-between items-center p-2 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center space-x-2">
              <span className="text-xs font-medium text-gray-700">
                {currentStep === 'month' && 'Step 1: Select Month'}
                {currentStep === 'day' && 'Step 2: Select Day'}
                {currentStep === 'year' && 'Step 3: Select Year'}
              </span>
              {currentStep !== 'month' && (
                <button
                  onClick={() => {
                    if (currentStep === 'day') {
                      setCurrentStep('month');
                      setSelectedMonth(null);
                    } else if (currentStep === 'year') {
                      setCurrentStep('day');
                      setSelectedDay(null);
                    }
                  }}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  ← Back
                </button>
              )}
            </div>
            <button
              onClick={() => {
                // Trigger save when closing via X button
                setIsEditing(false); // Done editing
                setValidationError(null); // Clear validation error
                if (onBlur && inputValue !== value) {
                  onBlur(inputValue);
                }
                setIsOpen(false);
                resetSelection();
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Preset options - always visible */}
          <div className="p-2 border-b border-gray-200 flex-shrink-0">
            <div className="text-xs font-medium text-gray-500 mb-1">Quick Select</div>
            <div className="grid grid-cols-4 gap-1">
              {presets.map((preset) => (
                <button
                  key={preset.label}
                  onClick={() => handlePresetClick(preset)}
                  className="px-2 py-1 text-xs border border-gray-200 hover:bg-gray-50 text-gray-700"
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          {/* Scrollable content area */}
          <div className="flex-1 overflow-y-auto">
            {/* Step 1: Month selector */}
            {currentStep === 'month' && (
              <div className="p-2">
                <div className="text-xs font-medium text-gray-500 mb-1">Select Month</div>
                <div className="grid grid-cols-4 gap-1">
                  {months.map((month, index) => (
                    <button
                      key={month}
                      onClick={() => handleMonthClick(index)}
                      className="px-2 py-1 text-xs border border-gray-200 hover:bg-blue-50 hover:border-blue-300 text-gray-700"
                    >
                      {month.substring(0, 3)} <span className="text-xs text-gray-400">{index + 1}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Day selector */}
            {currentStep === 'day' && selectedMonth !== null && (
              <div className="p-2">
                <div className="text-xs font-medium text-gray-500 mb-1">
                  Select Day in {months[selectedMonth]}
                </div>
                <div className="grid grid-cols-7 gap-1 text-center">
                  {/* Day headers */}
                  {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                    <div key={`day-header-${index}`} className="h-6 text-xs text-gray-500 flex items-center justify-center">
                      {day}
                    </div>
                  ))}
                  {/* Calendar grid */}
                  {renderCalendarGrid()}
                </div>
              </div>
            )}

            {/* Step 3: Year selector */}
            {currentStep === 'year' && selectedMonth !== null && selectedDay !== null && (
              <div className="p-2">
                <div className="text-xs font-medium text-gray-500 mb-1">
                  Select Year for {months[selectedMonth]} {selectedDay}
                </div>
                <div className="grid grid-cols-3 gap-1">
                  {years.map((year) => (
                    <button
                      key={year}
                      onClick={() => handleYearClick(year)}
                      className="px-2 py-1 text-xs border border-gray-200 hover:bg-blue-50 hover:border-blue-300 text-gray-700"
                    >
                      {year}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpirationDatePicker; 